import { useCallback, useEffect, useMemo, useState } from 'react';
import { FinancePageLayout } from '@/components/finance';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { CalendarCheck, Loader2, Lock, ShieldAlert, Unlock } from 'lucide-react';

type HealthRow = {
  ref_date: string;
  open_issues: number;
  high_critical_open: number;
  over_24h_open: number;
};

type PeriodCloseRow = {
  id: string;
  business_unit: 'all' | 'br' | 'us';
  period_year: number;
  period_month: number;
  status: 'open' | 'closed' | 'locked';
  closed_at: string | null;
  notes: string | null;
  created_at: string;
};

export default function ClosePeriod() {
  const now = new Date();
  const [year, setYear] = useState<number>(now.getUTCFullYear());
  const [month, setMonth] = useState<number>(now.getUTCMonth() + 1);
  const [businessUnit, setBusinessUnit] = useState<'all' | 'br' | 'us'>('all');
  const [closeNote, setCloseNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [health, setHealth] = useState<HealthRow | null>(null);
  const [rows, setRows] = useState<PeriodCloseRow[]>([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [healthRes, rowsRes] = await Promise.all([
        (supabase.from('v_finance_recon_d1_health' as any) as any).select('*').limit(1).maybeSingle(),
        (supabase.from('finance_period_close' as any) as any)
          .select('id,business_unit,period_year,period_month,status,closed_at,notes,created_at')
          .order('period_year', { ascending: false })
          .order('period_month', { ascending: false })
          .order('created_at', { ascending: false })
          .limit(24),
      ]);

      if (healthRes.error) throw healthRes.error;
      if (rowsRes.error) throw rowsRes.error;
      setHealth((healthRes.data || null) as HealthRow | null);
      setRows(((rowsRes.data || []) as unknown) as PeriodCloseRow[]);
    } catch (error: any) {
      console.error('close period load error:', error);
      toast.error(`Falha ao carregar fechamento: ${error.message || 'erro desconhecido'}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const selectedRow = useMemo(
    () => rows.find((r) => r.period_year === year && r.period_month === month && r.business_unit === businessUnit) || null,
    [rows, year, month, businessUnit],
  );

  useEffect(() => {
    setCloseNote(selectedRow?.notes || '');
  }, [selectedRow?.id]);

  const canClose = (health?.high_critical_open || 0) === 0 && (health?.over_24h_open || 0) === 0;
  const canLock = selectedRow?.status === 'closed';
  const isLocked = selectedRow?.status === 'locked';

  const upsertClose = async (status: 'open' | 'closed' | 'locked') => {
    setSaving(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      const { data: appUser } = await supabase
        .from('app_users')
        .select('id')
        .eq('auth_user_id', user?.id)
        .maybeSingle();

      const payload = {
        business_unit: businessUnit,
        period_year: year,
        period_month: month,
        status,
        closed_by: status === 'closed' || status === 'locked' ? appUser?.id ?? null : null,
        closed_at: status === 'closed' || status === 'locked' ? new Date().toISOString() : null,
        notes: closeNote || null,
      };

      const { error } = await (supabase.from('finance_period_close' as any) as any)
        .upsert(payload, { onConflict: 'business_unit,period_year,period_month' });
      if (error) throw error;

      if (status === 'closed') toast.success('Período fechado com sucesso');
      if (status === 'locked') toast.success('Período travado com sucesso');
      if (status === 'open') toast.success('Período reaberto');
      await loadData();
    } catch (error: any) {
      console.error('close period save error:', error);
      toast.error(`Falha ao salvar fechamento: ${error.message || 'erro desconhecido'}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <FinancePageLayout>
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-bold tracking-tight">Fechamento Mensal</h1>
        <Button variant="outline" onClick={loadData} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          Atualizar
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Issues abertas</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold">{health?.open_issues ?? 0}</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">High/Critical abertas</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold text-red-600">{health?.high_critical_open ?? 0}</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Abertas há mais de 24h</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold text-orange-600">{health?.over_24h_open ?? 0}</p></CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Controle de período</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label>Ano</Label>
              <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value || now.getUTCFullYear()))} />
            </div>
            <div>
              <Label>Mês</Label>
              <Input type="number" min={1} max={12} value={month} onChange={(e) => setMonth(Number(e.target.value || now.getUTCMonth() + 1))} />
            </div>
            <div>
              <Label>BU</Label>
              <Select value={businessUnit} onValueChange={(v: any) => setBusinessUnit(v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Consolidado</SelectItem>
                  <SelectItem value="br">BR</SelectItem>
                  <SelectItem value="us">US</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              {selectedRow ? (
                <Badge variant="outline">Status atual: {selectedRow.status}</Badge>
              ) : (
                <Badge variant="secondary">Sem registro ainda</Badge>
              )}
            </div>
          </div>

          <div>
            <Label>Nota de fechamento</Label>
            <Textarea value={closeNote} onChange={(e) => setCloseNote(e.target.value)} placeholder="Resumo do fechamento e observações" />
          </div>

          {!canClose ? (
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2 flex items-center gap-2">
              <ShieldAlert className="h-4 w-4" />
              Existem pendências críticas para D+1. Fechamento recomendado somente após saneamento.
            </div>
          ) : null}

          <div className="flex items-center gap-2">
            <Button onClick={() => upsertClose('closed')} disabled={saving || !canClose || isLocked}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CalendarCheck className="h-4 w-4 mr-2" />}
              Fechar período
            </Button>
            <Button variant="outline" onClick={() => upsertClose('locked')} disabled={saving || !canLock || isLocked}>
              <Lock className="h-4 w-4 mr-2" />
              Travar
            </Button>
            <Button variant="outline" onClick={() => upsertClose('open')} disabled={saving || !selectedRow}>
              <Unlock className="h-4 w-4 mr-2" />
              Reabrir
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Histórico recente</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {rows.length === 0 ? (
            <div className="text-sm text-muted-foreground">Sem histórico de fechamento.</div>
          ) : rows.map((r) => (
            <div key={r.id} className="border rounded-md p-2 text-sm flex flex-wrap items-center gap-2">
              <Badge variant="outline">{r.business_unit.toUpperCase()}</Badge>
              <span>{String(r.period_month).padStart(2, '0')}/{r.period_year}</span>
              <Badge>{r.status}</Badge>
              {r.closed_at ? <span className="text-muted-foreground">Fechado em {new Date(r.closed_at).toLocaleString()}</span> : null}
            </div>
          ))}
        </CardContent>
      </Card>
    </FinancePageLayout>
  );
}
